import React from 'react';
import { AngleUpIcon, AngleDownIcon } from '@c2fo/liquidity/icons';
import type { SortDirection } from '../../contexts/FileDeliveryContext';

interface TableHeaderBaseProps {
  label: string;
  className?: string;
  align?: 'left' | 'center' | 'right';
}

interface SortableTableHeaderProps<T extends string> extends TableHeaderBaseProps {
  field: T;
  currentSortField?: T;
  currentSortDirection?: SortDirection;
  onSort: (field: T) => void;
}

interface NonSortableTableHeaderProps extends TableHeaderBaseProps {
  field?: never;
  currentSortField?: never;
  currentSortDirection?: never;
  onSort?: never;
}

type TableHeaderProps<T extends string> = SortableTableHeaderProps<T> | NonSortableTableHeaderProps;

export const TableHeader = <T extends string>(props: TableHeaderProps<T>): React.ReactElement => {
  const { label, className = '', align = 'left' } = props;

  const alignClass = align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';
  const baseClass = `px-4 py-3 ${alignClass} text-xs font-medium text-gray-500 uppercase tracking-wider`;

  const isSortable = 'field' in props && 'onSort' in props && props.field && props.onSort;

  if (!isSortable) {
    return <th className={`${baseClass} ${className}`}>{label}</th>;
  }

  const { field, currentSortField, currentSortDirection, onSort } = props as SortableTableHeaderProps<T>;
  const isActive = currentSortField === field;

  return (
    <th
      className={`${baseClass} cursor-pointer hover:bg-gray-100 select-none ${className}`}
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        <span className="flex flex-col">
          {isActive && currentSortDirection === 'asc' && <AngleUpIcon className="h-3 w-3 text-primary-600" />}
          {isActive && currentSortDirection === 'desc' && <AngleDownIcon className="h-3 w-3 text-primary-600" />}
          {!isActive && (
            <span className="h-3 w-3 text-gray-300">
              <AngleUpIcon className="h-3 w-3 opacity-30" />
            </span>
          )}
        </span>
      </div>
    </th>
  );
};

export const SortableHeader = TableHeader;
